import { useCallback, useEffect, useState } from "react";

import {
  BenefitsBar,
  BrandCard,
  BrandView,
  Carousel,
  CartDrawer,
  CategoryMenu,
  Footer,
  Header,
  LoadingSpinner,
  MobileBottomNav,
  PriceComparison,
  ProductDetail,
  ProductGrid,
  ProductStrip,
  RankedProducts,
  SectionHeader
} from "../components";
import {
  marketplaceSearchCleared,
  marketplaceBrandProductsRequested,
  marketplaceHomeRequested,
  marketplaceProductsPageRequested,
  marketplaceSearchRequested
} from "../redux/actions/marketplace.actions";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { selectMarketplace } from "../redux/selectors/marketplace.selectors";
import type { CartItem, CartItemDraft } from "../types/cart";
import type { Brand, Product } from "../types/marketplace";
import { formatNumber } from "../utils/formatters";

const countdownIcon = "\u23F1";
const flashIcon = "\u26A1";
const trendingIcon = "\u{1F4C8}";
const allProductsPageSize = 50;

function createCartItemId(item: CartItemDraft): string {
  const productId = item.product.id ?? item.product.name;
  const variantId = item.variantId ?? item.variantLabel ?? "base";

  return `${productId}-${variantId}`;
}

function cartStoreKey(item: CartItem): string {
  return String(item.product.clientId ?? item.product.clientName ?? "tienda");
}

function HomeView() {
  const dispatch = useAppDispatch();
  const marketplace = useAppSelector(selectMarketplace);
  const [allProductsPage, setAllProductsPage] = useState<number>(1);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [comparedProducts, setComparedProducts] = useState<Product[]>([]);
  const [compareOpenRequestKey, setCompareOpenRequestKey] = useState<number>(0);
  const [isCartOpen, setIsCartOpen] = useState<boolean>(false);
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  useEffect(() => {
    dispatch(marketplaceHomeRequested());
  }, [dispatch]);

  useEffect(() => {
    if (selectedBrand) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [selectedBrand]);

  useEffect(() => {
    setAllProductsPage(1);
  }, [marketplace.productsMeta.total]);

  useEffect(() => {
    if (selectedBrand || marketplace.productsMeta.currentPage === allProductsPage) {
      return;
    }

    dispatch(marketplaceProductsPageRequested(allProductsPage, allProductsPageSize));
  }, [allProductsPage, dispatch, marketplace.productsMeta.currentPage, selectedBrand]);

  const handleSearchChange = (value: string) => {
    dispatch(marketplaceSearchRequested(value));
  };

  const handleSearchClear = () => {
    dispatch(marketplaceSearchCleared());
  };

  const handleHomeClick = () => {
    setSelectedBrand(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const scrollToElement = (id: string) => {
    setSelectedBrand(null);
    window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  const handleMobileSearchClick = () => {
    setSelectedBrand(null);
    window.setTimeout(() => {
      const input = document.getElementById("market-search-input") as HTMLInputElement | null;

      input?.focus();
      input?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
  };

  const handleMobileCompareClick = () => {
    if (comparedProducts.length >= 2) {
      setCompareOpenRequestKey((value) => value + 1);
      return;
    }

    scrollToElement("products-section");
  };

  const handleMobileCartClick = () => {
    setIsCartOpen(true);
  };

  const handleBrandProductsRequest = useCallback(
    (clientId: number, page: number, category?: string) => {
      dispatch(
        marketplaceBrandProductsRequested(clientId, page, allProductsPageSize, category)
      );
    },
    [dispatch]
  );

  const handleAddToCart = (draft: CartItemDraft) => {
    const id = createCartItemId(draft);

    setCartItems((items) => {
      const existingItem = items.find((item) => item.id === id);

      if (existingItem) {
        return items.map((item) => {
          if (item.id !== id) {
            return item;
          }

          const nextQuantity =
            item.stockLimit > 0
              ? Math.min(item.quantity + draft.quantity, item.stockLimit)
              : item.quantity + draft.quantity;

          return {
            ...item,
            quantity: nextQuantity
          };
        });
      }

      return [
        {
          ...draft,
          id
        },
        ...items
      ];
    });
    setSelectedProduct(null);
    setIsCartOpen(true);
  };

  const handleCartQuantityChange = (itemId: string, quantity: number) => {
    if (quantity <= 0) {
      setCartItems((items) => items.filter((item) => item.id !== itemId));
      return;
    }

    setCartItems((items) =>
      items.map((item) => {
        if (item.id !== itemId) {
          return item;
        }

        return {
          ...item,
          quantity: item.stockLimit > 0 ? Math.min(quantity, item.stockLimit) : quantity
        };
      })
    );
  };

  const handleCartRemove = (itemId: string) => {
    setCartItems((items) => items.filter((item) => item.id !== itemId));
  };

  const handleCartClearStore = (storeKey: string) => {
    setCartItems((items) => items.filter((item) => cartStoreKey(item) !== storeKey));
  };

  const handleCompareToggle = (product: Product) => {
    setComparedProducts((items) => {
      const exists = items.some((item) => item.id === product.id);

      if (exists) {
        return items.filter((item) => item.id !== product.id);
      }

      return [product, ...items].slice(0, 4);
    });
  };

  const handleCompareRemove = (product: Product) => {
    setComparedProducts((items) => items.filter((item) => item.id !== product.id));
  };

  const comparedProductIds = comparedProducts
    .map((product) => product.id)
    .filter((id): id is string => Boolean(id));

  const allProductsTotalPages = Math.max(1, marketplace.productsMeta.lastPage);
  const cartItemsCount = cartItems.reduce((total, item) => total + item.quantity, 0);
  const visibleProducts = marketplace.products;

  return (
    <div className="app">
      <Header
        cartItemsCount={cartItemsCount}
        onCartClick={() => setIsCartOpen(true)}
        onHomeClick={handleHomeClick}
        onProductSelect={setSelectedProduct}
        onSearchChange={handleSearchChange}
        onSearchClear={handleSearchClear}
        searchResults={marketplace.searchResults}
        searching={marketplace.searching}
      />

      {selectedBrand ? (
        <BrandView
          brand={selectedBrand}
          categories={marketplace.brandCategories}
          comparedProductIds={comparedProductIds}
          loading={marketplace.brandProductsLoading}
          meta={marketplace.brandProductsMeta}
          onBack={() => setSelectedBrand(null)}
          onCompareToggle={handleCompareToggle}
          onProductsRequest={handleBrandProductsRequest}
          onProductSelect={setSelectedProduct}
          products={marketplace.brandProducts}
        />
      ) : (
        <>
          <CategoryMenu categories={marketplace.categories} />
          <Carousel slides={marketplace.heroSlides} />
          <BenefitsBar benefits={marketplace.benefits} />
          <section className="marketplace-explain">
            <div className="container marketplace-explain-inner">
              <small>Cómo funciona Mika Market</small>
              <h2>Un marketplace de tiendas independientes</h2>
              <p>
                Mostramos productos publicados por distintas marcas para que puedas comparar y
                elegir mejor. La compra final se coordina directamente con cada tienda por sus
                canales, fuera de esta web, respetando sus políticas de pago, envío, cambios y
                garantía.
              </p>
            </div>
          </section>

          <main>
            {marketplace.error && marketplace.products.length === 0 && (
              <div className="container">
                <p className="api-error">{marketplace.error.message}</p>
              </div>
            )}

            {marketplace.loading && marketplace.products.length === 0 && (
              <div className="container">
                <LoadingSpinner label="Cargando marketplace..." />
              </div>
            )}

            {marketplace.searchResults.length > 0 && (
              <section className="container search-products-box">
                <SectionHeader title={"Resultados de b\u00fasqueda"} actionLabel="Limpiar" />
                <ProductGrid
                  comparedProductIds={comparedProductIds}
                  onCompareToggle={handleCompareToggle}
                  onProductSelect={setSelectedProduct}
                  products={marketplace.searchResults}
                />
              </section>
            )}

            <section className="container offers-section">
              <div className="offers-heading">
                <div className="offers-title-row">
                  <h2>
                    <span className="section-icon">{flashIcon}</span> Ofertas del d\u00eda
                  </h2>
                  <div className="countdown">{countdownIcon} 23:59:24</div>
                </div>
                <a href="#products-section">Ver todas</a>
              </div>
              <ProductStrip
                comparedProductIds={comparedProductIds}
                onCompareToggle={handleCompareToggle}
                onProductSelect={setSelectedProduct}
                products={marketplace.offers}
              />
            </section>

            <section className="brands-band" id="stores-section">
              <div className="container">
                <SectionHeader title="Tiendas destacadas" />
                <div className="brands-grid">
                  {marketplace.brands.map((brand) => (
                    <BrandCard
                      brand={brand}
                      key={brand.id ?? brand.name}
                      onSelect={setSelectedBrand}
                    />
                  ))}
                </div>
              </div>
            </section>

            <section className="container best-section">
              <SectionHeader icon={trendingIcon} title={"M\u00e1s vendidos"} />
              <RankedProducts
                comparedProductIds={comparedProductIds}
                onCompareToggle={handleCompareToggle}
                onProductSelect={setSelectedProduct}
                products={marketplace.bestSellers}
              />
            </section>

            <section className="container all-products-box" id="products-section">
              <h2>Mirá más productos para vos</h2>
              {marketplace.productsLoading ? (
                <LoadingSpinner label="Cargando productos..." />
              ) : (
                <ProductGrid
                  comparedProductIds={comparedProductIds}
                  onCompareToggle={handleCompareToggle}
                  onProductSelect={setSelectedProduct}
                  products={visibleProducts}
                />
              )}
              <div className="pagination-bar">
                <button
                  disabled={allProductsPage === 1 || marketplace.productsLoading}
                  onClick={() => setAllProductsPage((page) => Math.max(1, page - 1))}
                  type="button"
                >
                  Anterior
                </button>
                <span>
                  Página {formatNumber(allProductsPage)} de {formatNumber(allProductsTotalPages)}
                </span>
                <button
                  disabled={
                    allProductsPage === allProductsTotalPages || marketplace.productsLoading
                  }
                  onClick={() =>
                    setAllProductsPage((page) => Math.min(allProductsTotalPages, page + 1))
                  }
                  type="button"
                >
                  Siguiente
                </button>
              </div>
            </section>
          </main>
        </>
      )}

      <Footer benefits={marketplace.footerBenefits} columns={marketplace.footerColumns} />

      <PriceComparison
        openRequestKey={compareOpenRequestKey}
        onClear={() => setComparedProducts([])}
        onProductSelect={setSelectedProduct}
        onRemove={handleCompareRemove}
        products={comparedProducts}
      />

      <CartDrawer
        isOpen={isCartOpen}
        items={cartItems}
        onClearStore={handleCartClearStore}
        onClose={() => setIsCartOpen(false)}
        onQuantityChange={handleCartQuantityChange}
        onRemove={handleCartRemove}
      />

      <MobileBottomNav
        cartItemsCount={cartItemsCount}
        compareItemsCount={comparedProducts.length}
        onCartClick={handleMobileCartClick}
        onCompareClick={handleMobileCompareClick}
        onHomeClick={handleHomeClick}
        onProductsClick={() => scrollToElement("products-section")}
        onSearchClick={handleMobileSearchClick}
        onStoresClick={() => scrollToElement("stores-section")}
      />

      {selectedProduct && (
        <ProductDetail
          onAddToCart={handleAddToCart}
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}
    </div>
  );
}

export default HomeView;
